/**
 * Step 4, known and played (2.J deliverable 15): choose secrets or the pages of a type in the
 * target table, say what to mark them and as of when, then preview and apply. At most 1,000
 * targets at a time.
 */
import type { BootstrapLedgerV1 } from '@codex/contracts';
import { Button, CellMenu, type CellMenuOption, SegmentedControl, TextField } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import {
  LEDGER_KIND,
  ledgerApi,
  loadPageRows,
  loadSecretRows,
  targetsKey,
  useBatches,
  useSessions,
  useTypes,
} from '../data.js';
import { useDraft } from '../drafts.js';
import { TargetTable } from '../ledger/target-table.js';
import { batchRows } from '../model/step-undo.js';
import {
  asOfValue,
  clearSelection,
  type Mark,
  markChoices,
  NO_FILTER,
  NONE,
  overLimit,
  type Selection,
  selectShown,
  shownRows,
  TOO_MANY_TEXT,
  targetsOf,
  toggle,
} from '../model/targets.js';
import { StepPanel } from '../step-panel.js';
import { AppliedList, undoBatch } from '../step-undo.js';
import { useStep } from '../use-step.js';

type Source = 'secrets' | 'pages';
const MARK_WORDS: Readonly<Record<Mark, string>> = { known: 'Known', played: 'Played' };

export function LedgerStep() {
  const step = useStep(ledgerApi);
  const types = useTypes();
  const sessions = useSessions();
  const batches = useBatches(LEDGER_KIND);
  const [source, setSource] = useDraft<Source>('ledger:source', 'secrets');
  const [typeKey, setTypeKey] = useDraft<string | null>('ledger:type', null);
  const [filter, setFilter] = useDraft('ledger:filter', NO_FILTER);
  const [selection, setSelection] = useDraft<Selection>('ledger:selection', NONE);
  const [markChoice, setMarkChoice] = useDraft<Mark>('ledger:mark', 'known');
  const [asOf, setAsOf] = useDraft<string | null>('ledger:asof', null);

  const markable = (types.data ?? []).filter(
    (t) =>
      t.library === 'canon' &&
      !t.archived &&
      (t.capabilities.includes('discoverable') || t.capabilities.includes('playable')),
  );
  const type = markable.find((t) => t.key === typeKey);
  const loaded = useQuery({
    queryKey: [...targetsKey, source, type?.key ?? null],
    queryFn: () =>
      source === 'secrets' ? loadSecretRows() : loadPageRows(type?.key ?? '', type?.name ?? ''),
    enabled: source === 'secrets' || type !== undefined,
    retry: false,
  });
  const all = loaded.data ?? [];
  const shown = shownRows(all, filter);
  const choices = markChoices(source, type?.capabilities ?? []);
  const mark: Mark = choices.includes(markChoice) ? markChoice : (choices[0] ?? 'known');
  const states = [...new Set(all.map((r) => r.state))].sort();

  const asOfOptions: CellMenuOption<string>[] = [
    ...(sessions.data ?? [])
      .filter((s) => s.number !== null && (s.status === 'played' || s.status === 'processed'))
      .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
      .map((s) => ({ value: String(s.number), label: `Session ${s.number}: ${s.document.title}` })),
    { value: 'before_tracking', label: 'Before tracking' },
  ];

  const request: BootstrapLedgerV1 | null =
    selection.size === 0 || asOf === null || overLimit(selection) || choices.length === 0
      ? null
      : { targets: targetsOf(all, selection), mark, asOf: asOfValue(asOf) };
  const note = overLimit(selection)
    ? TOO_MANY_TEXT
    : selection.size === 0
      ? 'Choose at least one row to preview.'
      : asOf === null
        ? 'Choose when they were known.'
        : undefined;
  const change = (update: () => void) => {
    update();
    step.edit();
  };

  return (
    <form
      className="cx-bootstrap__step"
      onSubmit={(event) => {
        event.preventDefault();
        if (request !== null) void step.preview(request);
      }}
    >
      <p className="cx-bootstrap__muted">
        Mark secrets as known to the players, and pages as known or played, as of a session you
        played or from before tracking began.
      </p>
      <div className="cx-bootstrap__fields">
        <SegmentedControl<Source>
          label="Source"
          options={[
            { value: 'secrets', label: 'Secrets' },
            { value: 'pages', label: 'Pages' },
          ]}
          value={source}
          onChange={(next) =>
            change(() => {
              setSource(next);
              setSelection(clearSelection());
              setFilter(NO_FILTER);
            })
          }
        />
        {source === 'pages' && (
          <CellMenu
            label="Type"
            placeholder="Choose a type"
            options={markable.map((t) => ({ value: t.key, label: t.name }))}
            value={typeKey}
            onChange={(next) =>
              change(() => {
                setTypeKey(next);
                setSelection(clearSelection());
                setFilter(NO_FILTER);
              })
            }
          />
        )}
      </div>
      <div className="cx-bootstrap__fields">
        <TextField
          label="Filter"
          data-testid="bootstrap.ledger.filter"
          autoComplete="off"
          value={filter.text}
          onChange={(e) => setFilter({ ...filter, text: e.target.value })}
        />
        <CellMenu
          label="State"
          options={[
            { value: 'all', label: 'Any state' },
            ...states.map((value) => ({ value, label: value })),
          ]}
          value={filter.state}
          onChange={(state) => setFilter({ ...filter, state })}
        />
        <Button onClick={() => change(() => setSelection(selectShown(all, filter, selection)))}>
          Select all shown
        </Button>
        <Button onClick={() => change(() => setSelection(clearSelection()))}>
          Clear selection
        </Button>
        <span role="status">{`${selection.size} selected`}</span>
      </div>
      {loaded.isPending && (source === 'secrets' || type !== undefined) ? (
        <p role="status">Loading…</p>
      ) : loaded.isError ? (
        <p role="alert">The list couldn't be loaded.</p>
      ) : all.length === 0 && (source === 'secrets' || type !== undefined) ? (
        <p role="status">
          {source === 'secrets' ? 'There are no secrets yet.' : 'This type has no pages yet.'}
        </p>
      ) : (
        <TargetTable
          rows={shown}
          selection={selection}
          detailLabel={source === 'secrets' ? 'Held by' : 'Type'}
          onToggle={(key) => change(() => setSelection(toggle(selection, key)))}
        />
      )}
      <div className="cx-bootstrap__fields">
        <SegmentedControl<Mark>
          label="Mark"
          options={choices.map((value) => ({ value, label: MARK_WORDS[value] }))}
          value={choices.includes(mark) ? mark : null}
          onChange={(next) => change(() => setMarkChoice(next))}
        />
        <CellMenu
          label="As of"
          placeholder="Choose a session"
          options={asOfOptions}
          value={asOf}
          onChange={(next) => change(() => setAsOf(next))}
        />
      </div>
      <StepPanel
        state={step.state}
        canApply={step.canApply}
        previewDisabled={request === null}
        previewNote={note}
        onApply={() => void step.apply()}
      >
        <AppliedList
          rows={batchRows(batches.data ?? [], LEDGER_KIND)}
          undo={(row) => undoBatch(row.id)}
          onUndone={() => void step.refresh()}
        />
      </StepPanel>
    </form>
  );
}
